import React, { useCallback, useEffect, useRef, useState } from "react";
import { resolveSceneFit, resolveTargetRatio, computeStageBox } from "@/lib/sceneFit";
import { TAP_MAX_PX, DRAG_START_PX } from "@/lib/gestureThresholds";
import LetterboxBackdrop from "./LetterboxBackdrop";
import SceneMedia from "./SceneMedia";
import PanoramaScene from "./PanoramaScene";
import ZoneLayer from "./ZoneLayer";
import { computeFit } from "../lib/transitionHelpers";
import { MEDIA_VIGNETTE } from "../lib/mediaPresentation";

/**
 * Scene canvas — hosts the letterbox backdrop, current + previous scene
 * media, and the interactive zone layer. Encapsulates the pointer gesture
 * state machine that decides whether a drag becomes a scrub or a tap.
 *
 * Extracted verbatim from `PublicViewer.jsx` during the iter-38 refactor.
 * Behavior unchanged.
 */
export default function SceneCanvas({
  active = true, coveredByScrub = false, backdropManaged = false,
  scene, prevScene, frozenFrame, transitioning, fadeOutgoing = true, zones, inventory, scenes,
  onZoneClick, onHorizontalSwipe, onScrubProgress, onScrubRelease,
  lang, defaultLang, project, onActiveMediaStatus, onActiveMediaReady, onActiveMediaDisplayed, onActiveMediaDisplayFailed,
  panoramaHotspots, onPanoramaHotspot, highlightZoneIds, fieldDefinitions = [],
}) {
  const containerRef = useRef(null);
  const outerRef = useRef(null);
  const [viewport, setViewport] = useState({ w: 0, h: 0 });
  // NO fake default — zones must NEVER map against dimensions we invented.
  // null = "authoritative size unknown yet" and the ZoneLayer stays hidden
  // (fail closed) until the API lock or a real media report provides one.
  const [mediaSize, setMediaSize] = useState(null);
  // Which scene the current `mediaSize` belongs to. Zones are mapped using
  // `mediaSize`, so if we render them before the NEW scene's media reports
  // its dimensions (right after a slide) the polygons map against the
  // previous scene's dims → they look "detached". We only show the zone
  // layer once the active scene's media has loaded, keeping polygons locked
  // to their scene. (Snaps back correctly once loaded — matching the
  // "fixed on reopen" behaviour the user saw.)
  const [mediaSceneId, setMediaSceneId] = useState(null);
  // V1.1 — gesture state machine. `mode` evolves from null → 'scrub'|'fade'.
  // `scrubSign` records the sign of dx/dy required for forward progress
  // (negative for left/up, positive for right/down) so we can map any cumulative
  // drag distance directly into the [0,1] progress space the overlay expects.
  const gestureRef = useRef({
    active: false, mode: null, scrubSign: 0, scrubAxis: "x",
    x: 0, y: 0, t: 0,
  });
  const scrubReleaseCallbackRef = useRef(onScrubRelease);
  scrubReleaseCallbackRef.current = onScrubRelease;
  const cancelGesture = useCallback((pointerId) => {
    const gesture = gestureRef.current;
    if (!gesture.active || (pointerId !== undefined && gesture.pointerId !== undefined && gesture.pointerId !== pointerId)) return;
    gesture.active = false;
    if (gesture.mode === "scrub") scrubReleaseCallbackRef.current?.(false);
  }, []);
  useEffect(() => {
    const onBlur = () => cancelGesture();
    window.addEventListener("blur", onBlur);
    return () => window.removeEventListener("blur", onBlur);
  }, [cancelGesture]);
  const [previewZoneId, setPreviewZoneId] = useState(null);

  useEffect(() => {
    const el = outerRef.current;
    if (!el) return undefined;
    const upd = () => setViewport({ w: el.clientWidth, h: el.clientHeight });
    upd();
    // ResizeObserver on the ACTUAL stage host (layout can change without a
    // window resize — e.g. panels opening) + iOS visualViewport (URL bar /
    // keyboard collapse fires there, not always on window.resize).
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(upd) : null;
    ro?.observe(el);
    window.addEventListener("resize", upd);
    window.visualViewport?.addEventListener("resize", upd);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", upd);
      window.visualViewport?.removeEventListener("resize", upd);
    };
  }, []);

  // Close preview when changing scenes
  useEffect(() => { setPreviewZoneId(null); }, [scene?.id]);

  // iter48 — when the backend knows the scene's intrinsic media dimensions
  // (video scenes carry `media_width`/`media_height` probed at upload time),
  // lock the zone-mapping size to them IMMEDIATELY on scene change. This makes
  // interactive zones map correctly the instant the scene settles, instead of
  // waiting for the browser's <video> to report `videoWidth` (flaky/late on
  // iOS after rapid slides, which previously caused zones to "detach").
  useEffect(() => {
    if (scene?.media_width > 0 && scene?.media_height > 0) {
      setMediaSize({ w: scene.media_width, h: scene.media_height });
      setMediaSceneId(scene.id);
    }
  }, [scene?.id, scene?.media_width, scene?.media_height]);

  // Debug-only zone-alignment snapshot (?debug=1): logs everything needed to
  // diagnose drift — API dims, live mediaSize, real DOM rects of stage /
  // media / zone SVG, video decoder counts, last scrub mode. Snapshots are
  // kept on window.__zoneSnapshots (last 50) and printed to the console.
  useEffect(() => {
    if (typeof window === "undefined" || !/[?&]debug=1\b/.test(window.location.search)) return;
    if (!scene) return;
    const t = setTimeout(() => {
      const rect = (el) => { const r = el.getBoundingClientRect(); return { x: Math.round(r.x), y: Math.round(r.y), w: Math.round(r.width), h: Math.round(r.height) }; };
      const stageEl = containerRef.current;
      const mediaEl = stageEl?.querySelector('video[data-testid="scene-media-video"], img[data-testid="scene-media-image"], img[data-testid="scene-media-poster-base"], img[data-testid="scene-media-fallback-poster"]');
      const svgEl = stageEl?.querySelector('[data-testid="viewer-zone-layer"]');
      const snap = {
        at: new Date().toISOString(),
        activeSceneId: scene.id,
        sceneType: scene.type,
        apiDims: { w: scene.media_width || null, h: scene.media_height || null },
        mediaSize,
        mediaSceneId,
        zonesOnScene: zones?.length || 0,
        zonesFailClosed: (zones?.length || 0) > 0 && (!mediaSize || mediaSceneId !== scene.id),
        stageRect: stageEl ? rect(stageEl) : null,
        mediaTag: mediaEl?.tagName || null,
        mediaRect: mediaEl ? rect(mediaEl) : null,
        videoPlaying: mediaEl?.tagName === "VIDEO" ? !mediaEl.paused : null,
        svgRect: svgEl ? rect(svgEl) : null,
        svgViewBox: svgEl?.getAttribute("viewBox") || null,
        videosInDom: document.querySelectorAll("video").length,
        hiddenPreloaderVideos: document.querySelectorAll('[data-preloader="1"] video').length,
        prewarmVideos: document.querySelectorAll("body > video").length,
        scrub: window.__lastScrubDebug || null,
      };
      window.__zoneSnapshots = window.__zoneSnapshots || [];
      window.__zoneSnapshots.push(snap);
      if (window.__zoneSnapshots.length > 50) window.__zoneSnapshots.shift();
      // eslint-disable-next-line no-console
      console.info("[zoneSnapshot]", JSON.stringify(snap));
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene?.id, mediaSceneId, mediaSize, transitioning]);

  if (!scene) return null;

  // Phase 1 — Panorama 360 MVP. When the active scene is a panorama,
  // bypass video/zone logic entirely and hand off to PanoramaScene.
  if (scene.type === "panorama_360") {
    return (
      <div ref={outerRef} className="absolute inset-0" data-testid="viewer-scene-canvas">
        <PanoramaScene
          scene={scene}
          hotspots={panoramaHotspots || []}
          lang={lang}
          defaultLang={defaultLang}
          rtl={lang === "ar"}
          onHotspot={onPanoramaHotspot}
          onReady={() => { onActiveMediaReady?.(scene.id); onActiveMediaDisplayed?.(scene.id); }}
        />
      </div>
    );
  }
  // Resolve effective fit via the shared `resolveSceneFit` helper — the same
  // function SceneMedia uses, so zone polygon coordinates always map into
  // the same coordinate space that the actual <video>/<img> is rendered in.
  const display = resolveSceneFit(scene, project);
  const targetRatio = resolveTargetRatio(project);
  const stage = computeStageBox(viewport.w, viewport.h, targetRatio);
  const size = { w: stage.w, h: stage.h };
  const { scale, offsetX, offsetY } = computeFit(mediaSize?.w || 0, mediaSize?.h || 0, size.w, size.h, display);
  const mapPoint = (p) => ({ x: p.x * (mediaSize?.w || 0) * scale + offsetX, y: p.y * (mediaSize?.h || 0) * scale + offsetY });

  const debugGesture = (typeof window !== "undefined") && /[?&](debug|perf|touchdbg)=1\b/.test(window.location.search);
  const gestureLog = (label, extra) => {
    if (!debugGesture) return;
    const m = { ...(window.__viewerPerf?.measures || {}) };
    m[`gesture:${label}`] = JSON.stringify(extra || {});
    if (window.__viewerPerf) window.__viewerPerf.measures = m;
    console.info(`[gesture] ${label}`, extra || "");
  };

  const onPointerDown = (e) => {
    // Keep the captured gesture alive after its overlay mounts, but do not
    // replace it with another finger/gesture while navigation is inactive.
    if (!active || gestureRef.current.active) return;
    gestureRef.current = {
      active: true, mode: null, scrubSign: 0, scrubAxis: "x", pointerId: e.pointerId,
      x: e.clientX, y: e.clientY, t: performance.now(),
    };
    gestureLog("touchstart", { x: Math.round(e.clientX), y: Math.round(e.clientY), pointerType: e.pointerType, target: e.target?.tagName });
  };
  const onPointerMove = (e) => {
    const g = gestureRef.current;
    if (!g.active || (g.pointerId !== undefined && g.pointerId !== e.pointerId)) return;
    const dx = e.clientX - g.x;
    const dy = e.clientY - g.y;
    const absX = Math.abs(dx), absY = Math.abs(dy);

    // Still deciding what gesture this is? Detect on first move past
    // the shared tap/drag threshold (see lib/gestureThresholds.js — same
    // value used by ZoneLayer so there's no dead-zone between "tap"
    // and "drag").
    if (!g.mode) {
      if (Math.max(absX, absY) < DRAG_START_PX) return;
      const horizontal = absX > absY * 1.3;
      const vertical = absY > absX * 1.3;
      let dir = null;
      if (horizontal) dir = dx < 0 ? "left" : "right";
      else if (vertical) dir = dy < 0 ? "up" : "down";
      if (!dir) return;
      const handled = onHorizontalSwipe(dir);
      gestureLog("threshold_passed", { dx: Math.round(dx), dy: Math.round(dy), dir, handled });
      if (!handled) { g.active = false; return; }
      // Capture only after drag intent. Capturing on down steals zone taps.
      try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* ignore */ }
      g.mode = "scrub";
      g.scrubAxis = horizontal ? "x" : "y";
      g.scrubSign = (dir === "left" || dir === "up") ? -1 : 1;
    }

    if (g.mode === "scrub" && onScrubProgress) {
      // Compute scrub progress in [0,1]:
      //   For a left-swipe (scrubSign=-1) the user drags LEFT → dx negative;
      //     delta * scrubSign = (-200) * (-1) = +200 → progress = +0.2.
      //   For a right-swipe (scrubSign=+1)  dx positive → +200 * +1 = +200.
      // Either way, multiplying delta by scrubSign yields a non-negative
      // value when the user is progressing in the expected direction, which
      // we then normalise by the canvas dimension.
      const rect = e.currentTarget.getBoundingClientRect();
      const surfaceSize = g.scrubAxis === "x" ? rect.width : rect.height;
      const delta = g.scrubAxis === "x" ? dx : dy;
      const p = Math.max(0, Math.min(1, (delta * g.scrubSign) / Math.max(1, surfaceSize)));
      onScrubProgress(p);
    }
  };
  const onPointerUp = (e) => {
    const g = gestureRef.current;
    if (g.pointerId !== undefined && g.pointerId !== e.pointerId) return;
    if (!g.active) { gestureRef.current = { ...g, active: false }; return; }
    g.active = false;
    try { e.currentTarget.releasePointerCapture?.(e.pointerId); } catch { /* ignore */ }
    if (g.mode === "scrub" && onScrubRelease) {
      const rect = e.currentTarget.getBoundingClientRect();
      const surfaceSize = g.scrubAxis === "x" ? rect.width : rect.height;
      const delta = g.scrubAxis === "x" ? (e.clientX - g.x) : (e.clientY - g.y);
      const finalProgress = Math.max(0, Math.min(1, (delta * g.scrubSign) / Math.max(1, surfaceSize)));
      // Threshold 0.5 — past halfway, commit. Otherwise snap back.
      const dt = performance.now() - g.t;
      const velocity = Math.abs(delta) / Math.max(1, dt); // px/ms
      const commit = finalProgress >= 0.5 || (finalProgress > 0.12 && velocity > 0.5);
      gestureLog("touchend_scrub", { finalProgress: finalProgress.toFixed(2), velocity: velocity.toFixed(2), commit });
      onScrubRelease(commit);
      return;
    }
    if (g.mode === null) {
      const dx = e.clientX - g.x;
      const dy = e.clientY - g.y;
      gestureLog("touchend_tap", { dx: Math.round(dx), dy: Math.round(dy) });
      if (Math.max(Math.abs(dx), Math.abs(dy)) <= TAP_MAX_PX && !e.target.closest?.("[data-zone-interactive]")) setPreviewZoneId(null);
    }
  };

  // Helper for zone interaction: distinguishes "first tap = show preview" from "second tap = open details"
  // — moved into <ZoneLayer> in iter18 along with all zone rendering.

  return (
    <div ref={outerRef} className={`absolute inset-0${backdropManaged ? "" : " grain"}`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={(e) => cancelGesture(e.pointerId)}
      onLostPointerCapture={(e) => cancelGesture(e.pointerId)}
      data-testid="viewer-scene-canvas"
      data-scene-canvas
      style={{ touchAction: "none" }}
    >
      {/* Letterbox backdrop fills the viewport behind the cinematic stage. */}
      {!backdropManaged && !coveredByScrub && <LetterboxBackdrop scene={scene} project={project} />}
      {/* Fixed-ratio stage: when project.target_ratio is set, every scene
          renders into this identical centered box so transitions never
          change scale/crop. When off, the stage fills the viewport. */}
      <div
        ref={containerRef}
        data-testid="viewer-scene-stage"
        className="absolute overflow-hidden"
        style={targetRatio
          ? { left: stage.left, top: stage.top, width: stage.w, height: stage.h }
          : { inset: 0 }}
      >
        <SceneMedia key={`cur-${scene.id}`} active={active} scene={scene} project={project} instant={!!prevScene} onLoadedMedia={(w, h) => {
          // iter60 — the backend-probed dimensions (scene.media_width) are
          // authoritative. Ignore live browser reports for locked scenes so a
          // poster-fallback or stressed-decoder report can never remap zones.
          if (scene.media_width > 0 && scene.media_height > 0) return;
          setMediaSize({ w, h }); setMediaSceneId(scene.id);
        }} onStatus={onActiveMediaStatus} onReady={() => onActiveMediaReady?.(scene.id)}
          onDisplayed={() => onActiveMediaDisplayed?.(scene.id)} onDisplayFailed={() => onActiveMediaDisplayFailed?.(scene.id)} />
        {/* Frozen outgoing frame crossfades OUT on top of the incoming scene. */}
        {prevScene && <SceneMedia key={`prev-${prevScene.id}`} scene={prevScene} project={project} fadingOut holdOutgoing={!fadeOutgoing} frozenFrame={frozenFrame} />}
        {!backdropManaged && <div className="absolute inset-0 pointer-events-none" data-testid="viewer-media-vignette" style={{
          background: MEDIA_VIGNETTE
        }} />}
        {!coveredByScrub && !transitioning && !!mediaSize && mediaSceneId === scene.id && (
          <div className="absolute inset-0 z-30" data-testid="viewer-zone-ui">
          <ZoneLayer
            fieldDefinitions={fieldDefinitions}
            zones={zones}
            inventory={inventory}
            scenes={scenes}
            project={project}
            mapPoint={mapPoint}
            containerSize={size}
            previewZoneId={previewZoneId}
            setPreviewZoneId={setPreviewZoneId}
            onZoneClick={onZoneClick}
            lang={lang}
            defaultLang={defaultLang}
            highlightZoneIds={highlightZoneIds}
          />
          </div>
        )}
      </div>
    </div>
  );
}
